import {
  Alignment,
  BlockQuote,
  Bold,
  ClassicEditor,
  Essentials,
  Font,
  Heading,
  Highlight,
  HorizontalLine,
  Italic,
  Link,
  List,
  Mention,
  Paragraph,
  Strikethrough,
  TodoList,
  Underline,
} from "ckeditor5";
import "ckeditor5/ckeditor5.css";
import {
  Emoji,
  EmojiActivity,
  EmojiFlags,
  EmojiFood,
  EmojiNature,
  EmojiObjects,
  EmojiPeople,
  EmojiPlaces,
  EmojiSymbols,
} from "../../assets/emojiPlugin/src";
/*
 * Patches CKEditor 29.x renderer and DomConverter to handle null view-to-DOM
 * mappings gracefully. This fixes a crash when inserting multiple mentions
 * where the view tree and DOM tree become temporarily out of sync.
 * This can be removed once CKEditor is upgraded past v35+.
 */
const MentionSafety = function (editor) {
  const view = editor.editing.view;
  const renderer = view._renderer;
  const domConverter = view.domConverter;

  // Patch 1: Guard _updateDomSelection against null positions
  const originalUpdateDomSelection =
    renderer._updateDomSelection.bind(renderer);
  renderer._updateDomSelection = function (domRoot) {
    const domSelection = domRoot.ownerDocument.defaultView.getSelection();
    if (!this._domSelectionNeedsUpdate(domSelection)) {
      return;
    }
    const anchor = domConverter.viewPositionToDom(this.selection.anchor);
    const focus = domConverter.viewPositionToDom(this.selection.focus);
    if (!anchor || !focus) {
      return;
    }
    originalUpdateDomSelection(domRoot);
  };

  // Patch 2: Guard viewRangeToDom against null positions
  const originalViewRangeToDom = domConverter.viewRangeToDom.bind(domConverter);
  domConverter.viewRangeToDom = function (viewRange) {
    const domStart = this.viewPositionToDom(viewRange.start);
    const domEnd = this.viewPositionToDom(viewRange.end);
    if (!domStart || !domEnd) {
      return null;
    }
    return originalViewRangeToDom(viewRange);
  };

  // Patch 3: Make findCorrespondingDomText fall back to parent when
  // the previous sibling has no DOM mapping (e.g. mention attribute elements)
  const originalFindDomText =
    domConverter.findCorrespondingDomText.bind(domConverter);
  domConverter.findCorrespondingDomText = function (viewText) {
    const result = originalFindDomText(viewText);
    if (result) {
      return result;
    }
    // Fallback: previous sibling exists but has no DOM mapping.
    // Use parent element and walk DOM children to find the text node.
    const previousSibling = viewText.previousSibling;
    if (previousSibling && viewText.parent) {
      const domParent = this.mapViewToDom(viewText.parent);
      if (domParent) {
        // Count the view index of this text node
        let viewIndex = 0;
        for (const child of viewText.parent.getChildren()) {
          if (child === viewText) {
            break;
          }
          viewIndex++;
        }
        // Walk DOM children to find matching text node
        let domIndex = 0;
        for (let i = 0; i < domParent.childNodes.length; i++) {
          if (domIndex === viewIndex) {
            return domParent.childNodes[i];
          }
          domIndex++;
        }
      }
    }
    return null;
  };

  // Patch 4: Guard scrollToTheSelection against null range
  const originalScroll = view.scrollToTheSelection.bind(view);
  view.scrollToTheSelection = function () {
    const range = this.document.selection.getFirstRange();
    if (range) {
      const domRange = this.domConverter.viewRangeToDom(range);
      if (!domRange) {
        return;
      }
    }
    originalScroll();
  };
};

/*
 * This plugin customizes the way mentions are handled in the editor model and data.
 * Instead of a classic <span class="mention"></span>,
 */
export const MentionLinks = function (editor) {
  // The upcast converter will convert a view
  //
  //		<a href="..." class="mention" data-mention="...">...</a>
  //
  // element to the model "mention" text attribute.
  editor.conversion.for("upcast").elementToAttribute({
    view: {
      name: "a",
      key: "data-mention",
      classes: "mention",
      attributes: {
        "data-mention-id": true,
      },
    },
    model: {
      key: "mention",
      value: (viewItem) => {
        const mentionAttribute = editor.plugins
          .get("Mention")
          .toMentionAttribute(viewItem, {
            name: viewItem.getAttribute("href"),
            user_id: viewItem.getAttribute("data-mention-id"),
          });

        return mentionAttribute;
      },
    },
    converterPriority: "high",
  });

  // Downcast the model "mention" text attribute to a view
  //
  //		<a mentionId="..." class="mention" data-mention="...">...</a>
  //
  // element.
  editor.conversion.for("downcast").attributeToElement({
    model: "mention",
    view: (modelAttributeValue, { writer }) => {
      // Do not convert empty attributes (lack of value means no mention).
      if (!modelAttributeValue) {
        return;
      }

      return writer.createAttributeElement(
        "a",
        {
          class: "mention",
          "data-mention": modelAttributeValue.id,
          href: `mailto:${modelAttributeValue.name}`,
          "data-mention-id": modelAttributeValue.user_id,
        },
        {
          // Make mention attribute to be wrapped by other attribute elements.
          priority: 20,
          // Prevent merging mentions together.
          id: modelAttributeValue.uid,
        }
      );
    },
    converterPriority: "high",
  });
};

/*
 * Customizes the way the list of user suggestions is displayed.
 * Each user has an @id, a name and an avatar.
 * Group mentions display an icon instead of an avatar.
 */
const customItemRenderer = function (item) {
  const itemElement = document.createElement("div");
  const itemInfoElement = document.createElement("div");
  const userNameElement = document.createElement("span");
  const fullNameElement = document.createElement("span");

  itemElement.classList.add("mention__item", "d-flex", "align-items-center");
  itemInfoElement.classList.add("d-flex", "flex-column");

  // Check if this is a group mention
  if (item.isGroup) {
    // Create icon element for group mentions
    const iconWrapper = document.createElement("div");
    iconWrapper.classList.add("mention__item__group-icon");
    iconWrapper.style.cssText = `
      width: 32px;
      height: 32px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      margin-right: 8px;
      background-color: ${item.color || "#6e84a3"};
    `;

    const icon = document.createElement("i");
    icon.className = `fe ${item.icon || "fe-users"}`;
    icon.style.color = "#fff";
    icon.style.fontSize = "14px";

    iconWrapper.appendChild(icon);
    itemElement.appendChild(iconWrapper);

    fullNameElement.classList.add(
      "mention__item__full-name",
      "font-weight-bold"
    );
    fullNameElement.style.color = item.color || "#6e84a3";
    fullNameElement.textContent = item.name;

    userNameElement.classList.add("mention__item__user-name", "text-secondary");
    userNameElement.style.fontSize = "11px";
    userNameElement.textContent = item.description;
  } else {
    // Regular user mention - show avatar
    const avatar = document.createElement("img");
    avatar.src = require("@/assets/img/avatars/profiles/avatar.jpg");

    if (item.profile_picture) {
      avatar.src = item.profile_picture;
    }

    itemElement.appendChild(avatar);

    userNameElement.classList.add("mention__item__user-name", "text-secondary");
    userNameElement.textContent = item.name;

    fullNameElement.classList.add(
      "mention__item__full-name",
      "text-primary",
      "font-weight-bold"
    );
    fullNameElement.textContent = `${item.id.replace("@", "")}`;
  }

  itemInfoElement.appendChild(fullNameElement);
  itemInfoElement.appendChild(userNameElement);
  itemElement.appendChild(itemInfoElement);

  return itemElement;
};

export const getFeedItems = function (
  queryText,
  usersArray,
  groupMentionItems = []
) {
  // As an example of an asynchronous action, return a promise
  // that resolves after a 100ms timeout.
  // This can be a server request or any sort of delayed action.
  const users = usersArray.map((user) => {
    return {
      id: `@${user.text}`,
      user_id: user.id,
      name: user.value,
      profile_picture: user.profile_picture,
      email: user.email,
    };
  });

  const groups = groupMentionItems.map((group) => {
    return {
      id: group.marker,
      user_id: group.id, // e.g., "group:everyone"
      name: group.name,
      description: group.description,
      icon: group.icon,
      color: group.color,
      isGroup: true,
      count: group.count,
    };
  });

  return new Promise((resolve) => {
    setTimeout(() => {
      // Filter groups matching query
      const matchingGroups = groups.filter(isItemMatching);

      // Filter users matching query
      const matchingUsers = users.filter(isItemMatching);

      // Groups appear first, then users
      const itemsToDisplay = [...matchingGroups, ...matchingUsers];

      resolve(itemsToDisplay);
    }, 100);
  });

  // Filtering function - it uses the `name` and `id` properties of an item to find a match.
  function isItemMatching(item) {
    // Make the search case-insensitive.
    const searchString = queryText.toLowerCase();

    // Include an item in the search results if the name or id includes the current user input.
    return (
      item.name.toLowerCase().includes(searchString) ||
      item.id.toLowerCase().includes(searchString) ||
      (item.description &&
        item.description.toLowerCase().includes(searchString))
    );
  }
};

export const richTextConfig = {
  classicEditor: ClassicEditor,
  placeholder: "Type your message...",
  basicToolbar: {
    toolbar: {
      items: [
        "heading",
        "fontSize",
        "|",
        "fontColor",
        "fontBackgroundColor",
        "highlight",
        "|",
        "bold",
        "italic",
        "underline",
        "Strikethrough",
        "link",
        "blockQuote",
        "horizontalLine",
        "|",
        "numberedList",
        "bulletedList",
        "todoList",
        "alignment",
        "emoji",
        "|",
        "undo",
        "redo",
      ],
      shouldNotGroupWhenFull: true,
    },
    ui: {
      viewportOffset: {
        top: 76,
      },
    },
    fontSize: {
      options: [9, 11, 13, "default", 17, 19, 21, 23, 26],
    },
    fontColor: {
      colors: [
        {
          color: "hsl(0, 0%, 0%)",
          label: "Black",
        },
        {
          color: "hsl(0, 0%, 30%)",
          label: "Dim grey",
        },
        {
          color: "hsl(0, 0%, 60%)",
          label: "Grey",
        },
        {
          color: "hsl(0, 0%, 90%)",
          label: "Light grey",
        },
        {
          color: "hsl(0, 0%, 100%)",
          label: "White",
          hasBorder: true,
        },
        {
          color: "hsl(0, 75%, 60%)",
          label: "Red",
        },
        {
          color: "hsl(30, 75%, 60%)",
          label: "Orange",
        },
        {
          color: "hsl(60, 75%, 60%)",
          label: "Yellow",
        },
        {
          color: "hsl(90, 75%, 60%)",
          label: "Light green",
        },
        {
          color: "hsl(120, 75%, 60%)",
          label: "Green",
        },
        {
          color: "hsl(150, 75%, 60%)",
          label: "Aquamarine",
        },
        {
          color: "hsl(180, 75%, 60%)",
          label: "Turquoise",
        },
        {
          color: "hsl(210, 75%, 60%)",
          label: "Light blue",
        },
        {
          color: "hsl(240, 75%, 60%)",
          label: "Blue",
        },
        {
          color: "hsl(270, 75%, 60%)",
          label: "Purple",
        },
      ],
    },
    fontBackgroundColor: {
      colors: [
        {
          color: "hsl(0, 75%, 60%)",
          label: "Red",
        },
        {
          color: "hsl(30, 75%, 60%)",
          label: "Orange",
        },
        {
          color: "hsl(60, 75%, 60%)",
          label: "Yellow",
        },
        {
          color: "hsl(90, 75%, 60%)",
          label: "Light green",
        },
        {
          color: "hsl(120, 75%, 60%)",
          label: "Green",
        },
      ],
    },
    highlight: {
      options: [
        {
          model: "greenMarker",
          class: "marker-green",
          title: "Green marker",
          color: "var(--ck-highlight-marker-green)",
          type: "marker",
        },
        {
          model: "yellowMarker",
          class: "marker-yellow",
          title: "Yellow marker",
          color: "var(--ck-highlight-marker-yellow)",
          type: "marker",
        },
        {
          model: "redMarker",
          class: "marker-red",
          title: "light marker",
          color: "var(--ck-highlight-pen-red)",
          type: "marker",
        },
      ],
    },
  },
  plugins: [
    Essentials,
    Bold,
    Italic,
    Link,
    Paragraph,
    HorizontalLine,
    BlockQuote,
    Highlight,
    Underline,
    Strikethrough,
    List,
    Mention,
    Heading,
    Alignment,
    TodoList,
    Font,
    MentionSafety,
    MentionLinks,
    Emoji,
    EmojiPeople,
    EmojiNature,
    EmojiPlaces,
    EmojiFood,
    EmojiActivity,
    EmojiObjects,
    EmojiSymbols,
    EmojiFlags,
  ],
  feeds: {
    marker: "@",
    itemRenderer: customItemRenderer,
    minimumCharacters: 0,
  },
  getFeedItems: getFeedItems,
};
